import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PencilIcon,
  TrashIcon,
  ViewColumnsIcon,
  ListBulletIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  UserGroupIcon,
  PlusIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { getAllTemplates, deleteTemplate } from "../api/templates";
import { useAuth } from "../contexts/AuthContext";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import PermissionGuard from "../components/common/PermissionGuard";
import PagePermissionGuard from "../components/common/PagePermissionGuard";
import Pagination from "../components/Pagination";
import TemplateGridSkeleton from "../components/TemplateGridSkeleton";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { Button, Input, Checkbox, DataGrid } from "../components/ui";
import { hasDocumentTypeAccess } from "../utils/permissions";

const TEMPLATES_PAGE_LIMIT = 12;

const MyTemplates = () => {
  const { usage, permissions, activeCompany } = useAuth();
  const { setHeaderContent } = usePageHeader();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // 'list' (table-style) or 'grid' (cards)
  const [searchQuery, setSearchQuery] = useState("");
  const [availableDocumentTypes, setAvailableDocumentTypes] = useState([]);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState([]);
  const [isDocumentTypeDropdownOpen, setIsDocumentTypeDropdownOpen] =
    useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();

  // Filter templates based on document type permissions
  const filteredTemplates = useMemo(() => {
    if (!permissions || !permissions.templates) {
      return templates;
    }

    return templates.filter((template) => {
      const documentType = template.documentType || "";
      return hasDocumentTypeAccess(permissions, documentType);
    });
  }, [templates, permissions]);

  const fetchTemplates = async (
    search = "",
    documentTypes = [],
    page = 1,
    skipTypeUpdate = false,
  ) => {
    try {
      setIsLoading(true);
      const response = await getAllTemplates(
        search,
        documentTypes,
        page,
        TEMPLATES_PAGE_LIMIT,
      );
      const fetchedTemplates = Array.isArray(response.data)
        ? response.data
        : [];
      const pagination = response.pagination ?? {};
      const totalItemsCount = pagination.totalItems ?? fetchedTemplates.length;
      const totalPagesCount =
        pagination.totalPages ??
        (totalItemsCount > 0
          ? Math.ceil(Number(totalItemsCount) / TEMPLATES_PAGE_LIMIT)
          : 1);
      const serverPage = pagination.currentPage ?? page;

      setTemplates(fetchedTemplates);
      setTotalPages(totalPagesCount);
      setTotalItems(totalItemsCount);
      setCurrentPage(serverPage);

      // Merge document types from this page into available types; init selection on first load
      const typesFromPage = [
        ...new Set(
          fetchedTemplates
            .map((t) => t.documentType)
            .filter((type) => type && type.trim() !== ""),
        ),
      ].sort();
      if (typesFromPage.length > 0) {
        setAvailableDocumentTypes((prev) => {
          const merged = [...new Set([...prev, ...typesFromPage])].sort();
          return merged;
        });
        if (!skipTypeUpdate && !isInitialized) {
          setSelectedDocumentTypes(typesFromPage);
          setIsInitialized(true);
          skipNextDocumentTypeRefetchRef.current = true;
        }
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error(error.message || "Failed to fetch templates");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch - only runs once on mount
  useEffect(() => {
    fetchTemplates("", [], 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When user changes page (pagination), fetch that page
  useEffect(() => {
    if (!isInitialized) return;
    fetchTemplates(searchQuery, selectedDocumentTypes, currentPage, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Skip one refetch when selectedDocumentTypes is first set from initial load
  const skipNextDocumentTypeRefetchRef = useRef(false);

  // When document type filter changes (Select All / Deselect All / toggle), refetch immediately
  useEffect(() => {
    if (!isInitialized) return;
    if (skipNextDocumentTypeRefetchRef.current) {
      skipNextDocumentTypeRefetchRef.current = false;
      return;
    }
    setCurrentPage(1);
    fetchTemplates(searchQuery, selectedDocumentTypes, 1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocumentTypes]);

  // Debounce search and reset to page 1, then fetch
  useEffect(() => {
    if (!isInitialized) return;
    if (!searchQuery) return;
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchTemplates(searchQuery, selectedDocumentTypes, 1, true);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, isInitialized]);

  const handleDelete = async (templateId) => {
    setTemplateToDelete(templateId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteTemplate(templateToDelete);
      toast.success("Template deleted successfully");
      const pageToFetch =
        templates.length <= 1 && currentPage > 1
          ? currentPage - 1
          : currentPage;
      if (templates.length <= 1 && currentPage > 1)
        setCurrentPage(currentPage - 1);
      else
        fetchTemplates(searchQuery, selectedDocumentTypes, pageToFetch, true);
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(error.message || "Failed to delete template");
    } finally {
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Set page header for Navbar (title + company only; actions moved to page)
  useEffect(() => {
    setHeaderContent(
      <div>
        <BreadCrumb
          items={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Templates" },
          ]}
          className="mb-2"
        />
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
          My Templates
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl flex items-center gap-2 mt-1">
          Manage your templates and view details
          {activeCompany && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              {activeCompany.name}
            </span>
          )}
        </p>
      </div>,
    );
    return () => setHeaderContent(null);
  }, [activeCompany, setHeaderContent]);

  return (
    <PagePermissionGuard module="templates" action="read" pageName="Templates">
      <div className="px-6 py-5">
        {/* One row: Left = Document Type + Search; Right = Create buttons + View switch */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
            {/* Document Type Dropdown — matches Input CSS */}
            <div className="relative">
              <button
                type="button"
                onClick={() =>
                  setIsDocumentTypeDropdownOpen(!isDocumentTypeDropdownOpen)
                }
                className={`inline-flex items-center justify-between gap-2 w-full min-w-[200px] px-4 py-3 bg-white border text-sm font-medium text-gray-900 transition-all duration-200 ease-out rounded-xl hover:border-gray-400 focus:outline-none ${
                  isDocumentTypeDropdownOpen
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20"
                }`}
              >
                <span>
                  Document Type
                  {selectedDocumentTypes.length > 0 &&
                    selectedDocumentTypes.length <
                      availableDocumentTypes.length && (
                      <span className="ml-1 text-gray-500">
                        ({selectedDocumentTypes.length})
                      </span>
                    )}
                </span>
                <ChevronDownIcon
                  className={`h-4 w-4 text-gray-400 transition-transform ${
                    isDocumentTypeDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isDocumentTypeDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsDocumentTypeDropdownOpen(false)}
                  ></div>
                  <div className="absolute left-0 mt-1 z-20 w-64 bg-white border border-gray-300 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                    <div className="p-2">
                      <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-200">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            if (
                              selectedDocumentTypes.length ===
                              availableDocumentTypes.length
                            ) {
                              setSelectedDocumentTypes([]);
                            } else {
                              setSelectedDocumentTypes([
                                ...availableDocumentTypes,
                              ]);
                            }
                          }}
                        >
                          {selectedDocumentTypes.length ===
                          availableDocumentTypes.length
                            ? "Deselect All"
                            : "Select All"}
                        </Button>
                      </div>
                      {availableDocumentTypes.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No document types available
                        </div>
                      ) : (
                        availableDocumentTypes.map((type) => (
                          <Checkbox
                            key={type}
                            checked={selectedDocumentTypes.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDocumentTypes([
                                  ...selectedDocumentTypes,
                                  type,
                                ]);
                              } else {
                                setSelectedDocumentTypes(
                                  selectedDocumentTypes.filter(
                                    (t) => t !== type,
                                  ),
                                );
                              }
                            }}
                            label={type}
                            className="px-3 py-2 hover:bg-gray-50 rounded-lg -mx-1"
                          />
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Search Box */}
            <div className="flex-1 min-w-0 max-w-xl">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search templates by name..."
                icon={MagnifyingGlassIcon}
                iconPosition="left"
                size="default"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <PermissionGuard module="templates" action="write">
              <div
                className={`gemini-create-btn-wrapper mt-1 ${usage?.exceededTemplateCreation ? "pointer-events-none opacity-60" : ""}`}
              >
                <Link
                  to={
                    usage?.exceededTemplateCreation
                      ? "#"
                      : "/dashboard/templates/create-ai"
                  }
                  onClick={(e) => {
                    if (usage?.exceededTemplateCreation) {
                      e.preventDefault();
                      toast.error(
                        "You have reached the template creation limit for your plan. Please upgrade to create more templates.",
                      );
                    }
                  }}
                  className="gemini-create-btn"
                >
                  <span className="gemini-create-btn-inner">
                    <SparklesIcon className="h-5 w-5 text-[#4285f4] shrink-0" />
                    Create with AI
                  </span>
                </Link>
              </div>
              <Button
                as={Link}
                to={
                  usage?.exceededTemplateCreation
                    ? "#"
                    : "/dashboard/templates/create"
                }
                variant="primary"
                size="default"
                className="gap-2 py-3"
                disabled={usage?.exceededTemplateCreation}
                onClick={(e) => {
                  if (usage?.exceededTemplateCreation) {
                    e.preventDefault();
                    toast.error(
                      "You have reached the template creation limit for your plan. Please upgrade to create more templates.",
                    );
                  }
                }}
              >
                <PlusIcon className="h-5 w-5" />
                Create New Template
              </Button>
            </PermissionGuard>
            <div className="flex items-center bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2.5 rounded-lg transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-white text-primary shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="Grid View"
              >
                <ViewColumnsIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2.5 rounded-lg transition-all duration-200 ${
                  viewMode === "list"
                    ? "bg-white text-primary shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="List View"
              >
                <ListBulletIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <TemplateGridSkeleton
            viewMode={viewMode}
            count={TEMPLATES_PAGE_LIMIT}
          />
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            {/* <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" /> */}
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchQuery ? "No templates found" : "No templates"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery
                ? "Try adjusting your search query."
                : "Get started by creating a new template."}
            </p>
            <div className="mt-6">
              <PermissionGuard module="templates" action="write">
                <Link
                  to={
                    usage?.exceededTemplateCreation
                      ? "#"
                      : "/dashboard/templates/create"
                  }
                  onClick={(e) => {
                    if (usage?.exceededTemplateCreation) {
                      e.preventDefault();
                      toast.error(
                        "You have reached the template creation limit for your plan. Please upgrade to create more templates.",
                      );
                    }
                  }}
                  className={
                    usage?.exceededTemplateCreation ? "pointer-events-none" : ""
                  }
                >
                  <Button
                    variant="primary"
                    disabled={usage?.exceededTemplateCreation}
                  >
                    Create New Template
                  </Button>
                </Link>
              </PermissionGuard>
            </div>
          </div>
        ) : viewMode === "list" ? (
          <DataGrid
            columns={[
              {
                key: "template",
                label: "Template",
                sortable: false,
                render: (_, row) => (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <DocumentTextIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{row.name}</p>
                      <p className="text-xs text-gray-500 truncate max-w-[200px]">
                        {row.documentId?.fileName}
                      </p>
                    </div>
                  </div>
                ),
              },
              {
                key: "documentType",
                label: "Type",
                sortable: false,
                headerClassName: "hidden sm:table-cell",
                cellClassName: "hidden sm:table-cell",
                render: (val) =>
                  val ? (
                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                      {val}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  ),
              },
              {
                key: "recipients",
                label: "Recipients",
                sortable: false,
                headerClassName: "hidden md:table-cell",
                cellClassName: "hidden md:table-cell",
                render: (val) => (
                  <span className="text-sm text-gray-600">
                    {val?.length || 0} recipient
                    {(val?.length || 0) !== 1 ? "s" : ""}
                  </span>
                ),
              },
              {
                key: "createdAt",
                label: "Created",
                sortable: false,
                headerClassName: "hidden lg:table-cell",
                cellClassName: "hidden lg:table-cell",
                render: (val) => (
                  <span className="text-sm text-gray-500">
                    {val
                      ? new Date(val).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "—"}
                  </span>
                ),
              },
              {
                key: "actions",
                label: "Actions",
                sortable: false,
                render: (_, row) => (
                  <div className="flex items-center justify-end gap-2">
                    <PermissionGuard module="templates" action="update">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1.5"
                        onClick={() =>
                          navigate(
                            `/dashboard/templates/configure-document/${row.documentId._id}/${row._id}`,
                          )
                        }
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    </PermissionGuard>
                    <PermissionGuard module="templates" action="delete">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1.5 text-gray-500 hover:text-danger hover:bg-danger/10"
                        onClick={() => handleDelete(row._id)}
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </PermissionGuard>
                    <PermissionGuard module="templates" action="use_template">
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={
                          !row.documentConfigured || usage?.exceededSignRequests
                        }
                        onClick={() => {
                          if (usage?.exceededSignRequests) {
                            toast.error(
                              "You have reached the sign request limit for your plan. Please upgrade to send more sign requests.",
                            );
                            return;
                          }
                          navigate(
                            `/dashboard/templates/use-template/${row._id}`,
                          );
                        }}
                      >
                        {!row.documentConfigured
                          ? "Configure"
                          : usage?.exceededSignRequests
                            ? "Limit Reached"
                            : "Use"}
                      </Button>
                    </PermissionGuard>
                  </div>
                ),
              },
            ]}
            data={filteredTemplates.map((t) => ({ ...t, id: t._id }))}
            emptyMessage="No templates found"
            headerVariant="white"
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredTemplates.map((template) => (
              <div
                key={template._id}
                className="group bg-white rounded-3xl border border-gray-200 overflow-hidden transition-all duration-200"
              >
                {/* Content section — PDF icon, title, hover actions */}
                <div className="p-5">
                  <div className="flex justify-between items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 overflow-hidden">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 128 128"
                        id="pdf-file"
                      >
                        <path
                          fill="#fd4233"
                          d="M104 126H24c-5.514 0-10-4.486-10-10V12c0-5.514 4.486-10 10-10h40.687a9.932 9.932 0 0 1 7.07 2.929l39.314 39.314a9.932 9.932 0 0 1 2.929 7.07V116c0 5.514-4.486 10-10 10zM24 6c-3.309 0-6 2.691-6 6v104c0 3.309 2.691 6 6 6h80c3.309 0 6-2.691 6-6V51.313a6.044 6.044 0 0 0-1.757-4.242L68.929 7.757A5.96 5.96 0 0 0 64.687 6H24z"
                        ></path>
                        <path
                          fill="#ff402f"
                          d="M95.21 80.32c-.07-.51-.48-1.15-.92-1.58-1.26-1.24-4.03-1.89-8.25-1.95-2.86-.03-6.3.22-9.92.73-1.62-.93-3.29-1.95-4.6-3.18-3.53-3.29-6.47-7.86-8.31-12.89.12-.47.22-.88.32-1.3 0 0 1.98-11.28 1.46-15.1-.07-.52-.12-.67-.26-1.08l-.17-.44c-.54-1.25-1.6-2.57-3.26-2.5l-.98-.03h-.02c-1.86 0-3.36.95-3.76 2.36-1.2 4.44.04 11.09 2.29 19.69l-.58 1.4c-1.61 3.94-3.63 7.9-5.41 11.39l-.23.45c-1.88 3.67-3.58 6.79-5.13 9.43l-1.59.84c-.12.06-2.85 1.51-3.49 1.89-5.43 3.25-9.03 6.93-9.63 9.85-.19.94-.05 2.13.92 2.68l1.54.78c.67.33 1.38.5 2.1.5 3.87 0 8.36-4.82 14.55-15.62 7.14-2.32 15.28-4.26 22.41-5.32 5.43 3.05 12.11 5.18 16.33 5.18.75 0 1.4-.07 1.92-.21.81-.22 1.49-.68 1.91-1.3.82-1.23.98-2.93.76-4.67zM36.49 99.33c.7-1.93 3.5-5.75 7.63-9.13.26-.21.9-.81 1.48-1.37-4.32 6.89-7.21 9.63-9.11 10.5zM60.95 43c1.24 0 1.95 3.13 2.01 6.07.06 2.94-.63 5-1.48 6.53-.71-2.26-1.05-5.82-1.05-8.15 0 0-.05-4.45.52-4.45zm-7.3 40.14c.87-1.55 1.77-3.19 2.69-4.92 2.25-4.25 3.67-7.57 4.72-10.3 2.1 3.82 4.72 7.07 7.79 9.67.39.32.8.65 1.22.98-6.25 1.23-11.66 2.74-16.42 4.57zm39.43-.35c-.38.23-1.47.37-2.17.37-2.26 0-5.07-1.03-9-2.72 1.51-.11 2.9-.17 4.14-.17 2.27 0 2.94-.01 5.17.56 2.22.57 2.25 1.72 1.86 1.96z"
                        ></path>
                      </svg>
                    </div>
                    <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shrink-0">
                      <PermissionGuard module="templates" action="update">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/dashboard/templates/configure-document/${template.documentId._id}/${template._id}`,
                            );
                          }}
                          className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 hover:text-primary hover:bg-primary/10 transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard module="templates" action="delete">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(template._id);
                          }}
                          className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 hover:text-danger hover:bg-danger/10 transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                    </div>
                  </div>
                  <div className="flex justify-between items-start gap-3 mt-5">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-heading font-bold text-gray-900 truncate">
                        {template.name}
                      </h3>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-2 leading-relaxed">
                    {template.description || "No description available"}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <div className="inline-flex items-center gap-1.5 shrink-0 px-2 py-1 text-xs font-medium text-gray-500">
                      <UserGroupIcon className="h-5 w-5" />
                      <span>
                        {template.recipients?.length || 0} recipient
                        {(template.recipients?.length || 0) !== 1 ? "s" : ""}
                      </span>
                    </div>
                    {template.documentType && (
                      <span className="inline-flex justify-center items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs font-medium text-gray-500">
                        <DocumentTextIcon className="h-5 w-5" />
                        {template.documentType}
                      </span>
                    )}
                    {template.documentId?.isSequential && (
                      <span className="inline-flex px-2 py-0.5 rounded-lg text-xs font-medium text-gray-500">
                        <ClockIcon className="h-5 w-5" />
                        Sequential
                      </span>
                    )}
                  </div>
                </div>

                {/* Action section — date left, pill button right (reference) */}
                <div className="px-5 py-4 flex justify-between items-center border-t border-gray-100">
                  <span className="text-[12px] font-medium text-primary">
                    <p className="text-[10px] text-gray-500">
                      By {activeCompany?.name || "Your Company"}
                    </p>
                    {new Date(template.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                  <PermissionGuard module="templates" action="use_template">
                    <Button
                      variant="primary"
                      size="sm"
                      className="rounded-full px-5"
                      disabled={
                        !template.documentConfigured ||
                        usage?.exceededSignRequests
                      }
                      onClick={() => {
                        if (usage?.exceededSignRequests) {
                          toast.error(
                            "You have reached the sign request limit for your plan. Please upgrade to send more sign requests.",
                          );
                          return;
                        }
                        navigate(
                          `/dashboard/templates/use-template/${template._id}`,
                        );
                      }}
                    >
                      {!template.documentConfigured
                        ? "Configure"
                        : usage?.exceededSignRequests
                          ? "Limit Reached"
                          : "Use"}
                    </Button>
                  </PermissionGuard>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && filteredTemplates.length > 0 && totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setTemplateToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete"
          message="Are you sure you want to delete this template? This action cannot be undone."
        />
      </div>
    </PagePermissionGuard>
  );
};

export default MyTemplates;
